import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSectionTitle, UniversalToggle } from '@practium/ui';
import type { SystemPageMode, GroupFormData } from '../../../types';
import { ALL_SUBJECTS, ALL_ACTIONS, ALL_FEATURES } from '../../../constants/system.constants';
import { useGroupForm } from '../../../hooks/useGroupForm';
import styles from '../SystemForm.module.css';

export interface GroupFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface GroupFormProps {
	id?: string;
	mode: SystemPageMode;
	initialData?: GroupFormData;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
}

export const GroupForm = forwardRef<GroupFormRef, GroupFormProps>(function GroupForm(
	{ id, mode, initialData, onSubmit, onLoadingChange },
	ref
) {
	const {
		form,
		errors,
		isLoading,
		set,
		togglePermission,
		hasPermission,
		allMarked,
		toggleAll,
		getFeature,
		setFeature,
		handleSubmit,
		isDirty,
	} = useGroupForm({ id, initialData, onSubmit });

	useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

	useEffect(() => {
		onLoadingChange(isLoading);
	}, [isLoading, onLoadingChange]);

	const isView = mode === 'view';

	const permissionsTable = (
		<div className={styles.permissionsTableWrapper}>
			<table className={styles.permissionsTable}>
				<thead>
					<tr>
						<th>Nome</th>
						{ALL_ACTIONS.map((action) => (
							<th key={action.key}>{action.label}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{ALL_SUBJECTS.map((subject) => (
						<tr key={subject.key}>
							<td>
								<span className={styles.subjectLabel}>{subject.label}</span>
							</td>
							{ALL_ACTIONS.map((action) => (
								<td key={action.key} className={styles.toggleWrapper}>
									<UniversalToggle
										checked={hasPermission(subject.key, action.key)}
										onChangeToggle={
											isView ? undefined : () => togglePermission(subject.key, action.key)
										}
										disabled={isView}
									/>
								</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);

	const featuresTable = (
		<div className={styles.permissionsTableWrapper}>
			<table className={styles.permissionsTable}>
				<thead>
					<tr>
						<th>Funcionalidades</th>
						<th>Sim</th>
						<th>Não</th>
					</tr>
				</thead>
				<tbody>
					{ALL_FEATURES.map((feature) => (
						<tr key={feature.key}>
							<td>
								<span className={styles.subjectLabel}>{feature.label}</span>
							</td>
							<td>
								<input
									type="checkbox"
									className={styles.featureCheckbox}
									checked={getFeature(feature.key) === true}
									onChange={() => !isView && setFeature(feature.key, true)}
									disabled={isView}
								/>
							</td>
							<td>
								<input
									type="checkbox"
									className={styles.featureCheckbox}
									checked={getFeature(feature.key) === false}
									onChange={() => !isView && setFeature(feature.key, false)}
									disabled={isView}
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);

	if (isView) {
		return (
			<div className={styles.wrapper}>
				<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
				<div className={styles.viewGrid}>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Nome</span>
						<span className={styles.viewValue}>{form.name || '—'}</span>
					</div>

					<hr className={styles.sectionDivider} />
					<div className={styles.permissionsSection}>
						<div className={styles.fullWidth}>
							<FormSectionTitle>PERMISSÕES</FormSectionTitle>
						</div>
						{permissionsTable}
						{featuresTable}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<FormSectionTitle>INDENTIFICAÇÃO</FormSectionTitle>
			<div className={styles.formGrid}>
				<FormField label="Nome" required error={errors.name}>
					<FormInput
						value={form.name}
						onChange={(e) => set('name', e.target.value)}
						placeholder="Ex.: Total"
						hasError={!!errors.name}
					/>
				</FormField>

				<hr className={styles.sectionDivider} />
				<div className={styles.permissionsSection}>
					<div className={styles.fullWidth}>
						<FormSectionTitle>PERMISSÕES</FormSectionTitle>
					</div>
					<div className={styles.permissionsHeader}>
						<div className={styles.toggleWrapper}>
							<UniversalToggle checked={allMarked} onChangeToggle={toggleAll} />
						</div>
						<span className={styles.markAllLabel}>Marcar Todos</span>
					</div>
					{permissionsTable}
					{featuresTable}
				</div>
			</div>
		</div>
	);
});
